import { type ReactNode } from 'react'

import { InitialsAvatar } from '@/components/ui/initials-avatar'
import { cn } from '@/lib/utils'

export type PersonIdentityCellProps = {
  name: string
  /** Muted second line — email, role, expiry note. */
  subline?: ReactNode
  avatarUrl?: string | null
  colorSeed: string
  /** `sm` — tables; `md` — settings/team rows; `lg` — detail hero. */
  size?: 'sm' | 'md' | 'lg'
  /** Inline chips beside the name (e.g. "You" / "Pending" badge). */
  nameAside?: ReactNode
  /** Content above the name row (e.g. status badges on detail hero). */
  header?: ReactNode
  /** Content below the subline (e.g. access badges on embedded detail). */
  footer?: ReactNode
  nameClassName?: string
  avatarClassName?: string
  className?: string
}

const AVATAR_SIZE_CLASS = {
  sm: 'size-8',
  md: 'size-9',
  lg: 'size-16',
} as const

const SUBLINE_SIZE_CLASS = {
  sm: 'text-xs',
  md: 'text-xs',
  lg: 'text-sm',
} as const

const NAME_SIZE_CLASS = {
  sm: 'text-sm',
  md: 'text-sm',
  lg: 'text-xl font-semibold leading-tight',
} as const

export function PersonIdentityCell({
  name,
  subline,
  avatarUrl,
  colorSeed,
  size = 'md',
  nameAside,
  header,
  footer,
  nameClassName,
  avatarClassName,
  className,
}: PersonIdentityCellProps) {
  return (
    <div className={cn('flex min-w-0 items-center gap-3', size === 'lg' && 'items-start gap-4', className)}>
      <InitialsAvatar
        src={avatarUrl}
        colorSeed={colorSeed}
        className={cn(AVATAR_SIZE_CLASS[size], 'shrink-0 border-0', avatarClassName)}
        alt={name}
      />
      <div className="min-w-0 space-y-1">
        {header}
        <div className={cn('flex min-w-0 flex-wrap items-center gap-2', size === 'lg' && 'space-y-2')}>
          <p className={cn('truncate font-medium', NAME_SIZE_CLASS[size], nameClassName)}>{name}</p>
          {nameAside}
        </div>
        {subline ? (
          <div className={cn('truncate text-muted-foreground', SUBLINE_SIZE_CLASS[size])}>{subline}</div>
        ) : null}
        {footer}
      </div>
    </div>
  )
}
